import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Award, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { TrustSection } from "@/components/TrustSection";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: {
    overline: "Qui sommes-nous", title: "Une expertise dédiée à votre réussite",
    histOverline: "Notre histoire", histTitle: "Work & Travel Expert LLC",
    p1: "Basée en Floride (États-Unis), Work & Travel Expert LLC accompagne depuis plusieurs années les personnes souhaitant vivre, travailler et s'installer en Australie ou au Canada. Notre mission : rendre l'immigration simple, transparente et sereine.",
    p2: "De l'évaluation de votre profil à l'obtention de votre visa, nous mettons notre expertise et notre réseau au service de votre projet de vie.",
    discover: "Découvrir nos packs",
    mission: "Notre mission", missiont: "Simplifier chaque étape de votre projet d'immigration avec rigueur et bienveillance.",
    vision: "Notre vision", visiont: "Devenir la référence francophone de l'accompagnement vers l'Australie et le Canada.",
    values: "Nos valeurs", valuest: "Transparence, confiance, excellence et engagement humain à chaque instant.",
  },
  en: {
    overline: "About us", title: "Expertise dedicated to your success",
    histOverline: "Our story", histTitle: "Work & Travel Expert LLC",
    p1: "Based in Florida (USA), Work & Travel Expert LLC has been supporting people who wish to live, work and settle in Australia or Canada for several years. Our mission: to make immigration simple, transparent and stress-free.",
    p2: "From assessing your profile to obtaining your visa, we put our expertise and network at the service of your life project.",
    discover: "Discover our packages",
    mission: "Our mission", missiont: "Simplify every step of your immigration project with rigor and care.",
    vision: "Our vision", visiont: "Become the leading French-speaking reference for support to Australia and Canada.",
    values: "Our values", valuest: "Transparency, trust, excellence and human commitment at all times.",
  },
};

export default function About() {
  const { lang } = useLang();
  const c = C[lang];
  return (
    <div data-testid="about-page">
      <Seo
        title="À propos — Work & Travel Expert LLC | Experts visa Australie & Canada"
        description="Basée en Floride (États-Unis), Work & Travel Expert LLC accompagne les francophones vers l'Australie et le Canada. Notre mission : une immigration simple, transparente et sereine."
        keywords="work travel expert, agence immigration francophone, accompagnement visa, à propos"
        path="/a-propos"
      />
      <div className="relative bg-navy">
        <img src={IMAGES.about} alt="Équipe" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-navy/70" />
        <Section className="relative z-10 py-24">
          <Overline>{c.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white max-w-3xl">{c.title}</h1>
        </Section>
      </div>

      <Section className="py-24 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>{c.histOverline}</Overline>
          <h2 className="mt-4 text-3xl sm:text-4xl font-medium text-navy">{c.histTitle}</h2>
          <div className="gold-line mt-6" />
          <p className="mt-6 text-slate-700 leading-relaxed">{c.p1}</p>
          <p className="mt-4 text-slate-700 leading-relaxed">{c.p2}</p>
          <Link to="/nos-packs" className="inline-flex items-center gap-2 mt-8 bg-gold hover:bg-gold-hover text-white px-8 py-4 font-medium transition-colors">
            {c.discover} <ArrowRight size={18} />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={IMAGES.passport} alt="Documents de voyage" className="w-full h-[480px] object-cover" />
        </Reveal>
      </Section>

      <div className="bg-[#F8F9FA]">
        <Section className="py-24">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: c.mission, text: c.missiont },
              { icon: Eye, title: c.vision, text: c.visiont },
              { icon: Award, title: c.values, text: c.valuest },
            ].map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="bg-white border border-border p-8 h-full hover:border-gold transition-colors">
                  <div className="h-12 w-12 bg-navy text-gold flex items-center justify-center"><v.icon size={22} /></div>
                  <h3 className="font-heading text-2xl text-navy mt-5">{v.title}</h3>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* CONFIANCE & TRANSPARENCE — CERTIFICAT OFFICIEL */}
      <TrustSection theme="light" />
    </div>
  );
}
